import type { LucideIcon } from "lucide-react";
import { FileText, ListChecks, Loader2, Plus, Receipt, Trash2 } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import type { LegalDocumentType } from "@/components/features/notarial-requests/legal-entity-request-fields";
import { CjppuCertificateInstructionSteps } from "@/components/shared/CjppuCertificateInstructions";
import {
  IncomeDocumentGuideSection,
  IncomeGuideExternalLink,
} from "@/components/shared/IncomeDocumentGuideSection";
import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { WizardStepHeading } from "@/components/shared/WizardStepHeading";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ChoiceCardGroup,
  type ChoiceCardOption,
} from "@/components/ui/choice-card-group";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  PRESENTING_ENTITY_KEYS_WITH_INSTITUTION,
  PRESENTING_INSTITUTIONS,
  PRESENTING_INSTITUTION_OTHER,
} from "@/constants/presentingInstitutions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  isValidateRutServiceUnavailableError,
  validateRut,
  type ValidateRutResponse,
} from "@/services/authService";
import {
  EMPTY_MANUAL_COMPANY_FIELDS,
  manualCompanyFieldsToValidateRutResponse,
} from "@/utils/manualCompanySnapshot";
import { DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED } from "@/constants/certificateFulfillmentModes";
import { MAX_INCOME_CERTIFICATE_BILLABLE_FILES } from "@/services/certificatePricingCatalogService";
import type {
  IncomeActivityKey,
  IncomeCertificateWizardFormState,
} from "@/hooks/useIncomeCertificateWizardFormState";
import type {
  IncomeCertificateDetailsBody,
  IncomeCertificateWorkModality,
  UserNotarialRequestDocumentType,
} from "@/services/userNotarialRequestsService";

export type { IncomeActivityKey } from "@/hooks/useIncomeCertificateWizardFormState";

const DGI_PORTAL_URL = "https://www.dgi.gub.uy/";
const BPS_ONLINE_SERVICES_URL = "https://www.bps.gub.uy/";

function IncomeUploadCard({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string;
  description: string;
  icon: LucideIcon;
  children: ReactNode;
}): JSX.Element {
  return (
    <Card className="overflow-hidden border-border/80 bg-card shadow-sm dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20">
      <CardHeader className="space-y-0 pb-4">
        <div className="flex items-start gap-3 sm:gap-4">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-border/80 bg-muted/50 text-primary shadow-sm transition-colors duration-150 dark:border-border/80 dark:bg-secondary/50"
            aria-hidden
          >
            <Icon className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1 space-y-1.5">
            <CardTitle className="text-lg font-semibold leading-snug tracking-tight text-foreground">
              {title}
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">{children}</CardContent>
    </Card>
  );
}

/** Preset labels are translated; value sent to the API is the resolved label or custom text for `other`. */
const PRESENTING_ENTITY_KEYS = [
  "bank",
  "landlord_or_real_estate",
  "consulate",
  "employer",
  "university",
  "government",
  "lender",
  "other",
] as const;
type PresentingEntityPreset = (typeof PRESENTING_ENTITY_KEYS)[number];

/** Must stay in sync with backend `INCOME_CERTIFICATE_ACTIVITY_VALUES`. */
const ACTIVITY_KEYS = [
  "lawyer",
  "architect",
  "merchant",
  "accountant",
  "engineer",
  "retired",
  "doctor",
  "other",
] as const;

export interface IncomeCertificateSubmitPayload {
  fulfillmentMode: string;
  incomeCertificateDetails: IncomeCertificateDetailsBody;
  companySnapshot?: {
    legalName?: string;
    tradeName?: string;
    rut?: string;
    legalAddress?: string;
    city?: string;
    department?: string;
    postalCode?: string;
    phone?: string;
    email?: string;
    fechaInicio?: string;
    economicActivity?: string;
  };
  uploads: Array<{ type: UserNotarialRequestDocumentType; file: File }>;
  /** True when company RUT was entered manually because registry lookup was unavailable. */
  companyRutUnverified?: boolean;
}

export interface IncomeCertificateWizardProps {
  /** From certificate pricing catalog for `income_certificate`. */
  allowedFulfillmentModes?: string[];
  /** All field state lives in the parent flow so it survives wizard remounts. */
  form: IncomeCertificateWizardFormState;
  innerStep: 1 | 2 | 3;
  onInnerStepChange: (step: 1 | 2 | 3) => void;
  onPrimaryEnabledChange?: (enabled: boolean) => void;
  onSuccess: (requestId: string) => void;
  createWithUpload: (payload: IncomeCertificateSubmitPayload) => Promise<string>;
  /** When set, step 3 primary validates and calls this instead of creating the request (payment happens on the summary step). */
  onStep3ContinueToReview?: (payload: IncomeCertificateSubmitPayload) => void;
}

export interface IncomeCertificateWizardHandle {
  runPrimaryAction: () => Promise<void>;
  /** When on step 3 and uploads are valid, returns payload for payment + create; otherwise null. */
  prepareSubmitPayloadIfValid: () => IncomeCertificateSubmitPayload | null;
}

export const IncomeCertificateWizard = forwardRef<
  IncomeCertificateWizardHandle,
  IncomeCertificateWizardProps
>(function IncomeCertificateWizard(
  {
    allowedFulfillmentModes: allowedFulfillmentModesProp,
    form,
    innerStep,
    onInnerStepChange,
    onPrimaryEnabledChange,
    onSuccess,
    createWithUpload,
    onStep3ContinueToReview,
  },
  ref,
): JSX.Element {
  const { t } = useTranslation();

  const {
    fulfillmentMode,
    setFulfillmentMode,
    periodMonths,
    setPeriodMonths,
    presentingEntityKey,
    setPresentingEntityKey,
    presentingEntityOther,
    setPresentingEntityOther,
    presentingInstitution,
    setPresentingInstitution,
    presentingInstitutionOther,
    setPresentingInstitutionOther,
    activity,
    setActivity,
    activityCustom,
    setActivityCustom,
    specialFormatRequested,
    setSpecialFormatRequested,
    specialFormatFile,
    setSpecialFormatFile,
    workModality,
    setWorkModality,
    salaryReceipts,
    setSalaryReceipts,
    companyRut,
    setCompanyRut,
    validatedCompany,
    setValidatedCompany,
    companyRutManualMode,
    setCompanyRutManualMode,
    incomeManualCompanyFields,
    setIncomeManualCompanyFields,
    updateIncomeManualCompanyField,
    isValidatingRut,
    setIsValidatingRut,
    dgiFile,
    setDgiFile,
    tarjetaRutFile,
    setTarjetaRutFile,
    form6906File,
    setForm6906File,
    bpsNumber,
    setBpsNumber,
    bpsFile,
    setBpsFile,
    invoices,
    setInvoices,
    cajaFile,
    setCajaFile,
    legalDocumentType,
    legalDocumentFrontFile,
    setLegalDocumentFrontFile,
    legalDocumentBackFile,
    setLegalDocumentBackFile,
    passportDocumentFile,
    setPassportDocumentFile,
    handleLegalDocumentTypeChange,
    lockedFields,
    enterpriseDefaults,
    enterpriseName,
  } = form;

  const allowedFulfillmentModes =
    allowedFulfillmentModesProp != null && allowedFulfillmentModesProp.length > 0
      ? allowedFulfillmentModesProp
      : [...DEFAULT_CERTIFICATE_FULFILLMENT_MODES_ALLOWED];

  const resolvedPresentingEntity = useMemo((): string => {
    if (!presentingEntityKey) return "";
    if (presentingEntityKey === "other") return presentingEntityOther.trim();
    return t(`requests.user.incomeWizard.presentingEntityOption.${presentingEntityKey}`);
  }, [presentingEntityKey, presentingEntityOther, t]);

  // Naming a specific institution only makes sense for banks and lenders; every
  // other category already captures the specific body in presentingEntityOther.
  const showsPresentingInstitution = useMemo(
    () =>
      presentingEntityKey !== "" &&
      PRESENTING_ENTITY_KEYS_WITH_INSTITUTION.includes(presentingEntityKey),
    [presentingEntityKey],
  );

  const presentingInstitutionOptions = useMemo(
    () => [
      ...PRESENTING_INSTITUTIONS,
      {
        value: PRESENTING_INSTITUTION_OTHER,
        label: t("requests.user.incomeWizard.presentingInstitutionOther"),
      },
    ],
    [t],
  );

  /** Canonical name for the API: the free text when "other", else the label. */
  const resolvedPresentingInstitution = useMemo(() => {
    if (!showsPresentingInstitution || !presentingInstitution) return "";
    if (presentingInstitution === PRESENTING_INSTITUTION_OTHER) {
      return presentingInstitutionOther.trim();
    }
    return (
      PRESENTING_INSTITUTIONS.find((i) => i.value === presentingInstitution)?.label ?? ""
    );
  }, [showsPresentingInstitution, presentingInstitution, presentingInstitutionOther]);

  const incomeEffectiveValidatedCompany = useMemo((): ValidateRutResponse | null => {
    if (validatedCompany != null) return validatedCompany;
    if (companyRutManualMode && incomeManualCompanyFields.legalName.trim() !== "") {
      return manualCompanyFieldsToValidateRutResponse(incomeManualCompanyFields);
    }
    return null;
  }, [validatedCompany, companyRutManualMode, incomeManualCompanyFields]);

  const isStep1Complete = useMemo(
    () =>
      fulfillmentMode !== "" &&
      allowedFulfillmentModes.includes(fulfillmentMode) &&
      activity !== "" &&
      resolvedPresentingEntity.length > 0 &&
      (activity !== "other" || activityCustom.trim().length > 0) &&
      (!specialFormatRequested || specialFormatFile != null || lockedFields.has('specialFormatRequested')),
    [
      activity,
      activityCustom,
      allowedFulfillmentModes,
      fulfillmentMode,
      lockedFields,
      resolvedPresentingEntity,
      specialFormatFile,
      specialFormatRequested,
    ],
  );

  const isIdSectionComplete = useMemo((): boolean => {
    if (!legalDocumentType) return false;
    if (legalDocumentType === "national_id") {
      return legalDocumentFrontFile != null && legalDocumentBackFile != null;
    }
    return passportDocumentFile != null;
  }, [
    legalDocumentType,
    legalDocumentFrontFile,
    legalDocumentBackFile,
    passportDocumentFile,
  ]);

  const isStep2Complete = useMemo(() => {
    if (!workModality) return false;
    if (!isIdSectionComplete) return false;
    if (workModality === "dependent") {
      return true;
    }
    const digits = companyRut.replace(/\D/g, "");
    if (digits.length !== 12 || incomeEffectiveValidatedCompany == null) return false;
    if (dgiFile == null || tarjetaRutFile == null || bpsFile == null || !bpsNumber.trim()) {
      return false;
    }
    if (form6906File == null) return false;
    if (workModality === "professional_independent" && cajaFile == null) return false;
    return true;
  }, [
    workModality,
    isIdSectionComplete,
    companyRut,
    incomeEffectiveValidatedCompany,
    dgiFile,
    tarjetaRutFile,
    form6906File,
    bpsFile,
    bpsNumber,
    cajaFile,
  ]);

  const isStep3Complete = useMemo(() => {
    if (workModality === "dependent") {
      return (
        salaryReceipts.length >= periodMonths &&
        salaryReceipts.length <= MAX_INCOME_CERTIFICATE_BILLABLE_FILES &&
        salaryReceipts.every((f) => f != null)
      );
    }
    if (workModality === "independent" || workModality === "professional_independent") {
      return (
        invoices.length >= periodMonths &&
        invoices.length <= MAX_INCOME_CERTIFICATE_BILLABLE_FILES &&
        invoices.every((f) => f != null)
      );
    }
    return false;
  }, [workModality, salaryReceipts, invoices, periodMonths]);

  useEffect(() => {
    const enabled =
      innerStep === 1
        ? isStep1Complete
        : innerStep === 2
          ? isStep2Complete
          : isStep3Complete;
    onPrimaryEnabledChange?.(enabled);
  }, [
    innerStep,
    isStep1Complete,
    isStep2Complete,
    isStep3Complete,
    onPrimaryEnabledChange,
  ]);

  // When the user picks a work modality on step 2, the identity/company fields
  // appear inline below the radio cards. On narrow mobile they're below the
  // fold, so we scroll them into view to make the reveal obvious. No-op on
  // desktop where the reveal is already visible.
  const modalityRevealRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (innerStep !== 2 || workModality == null) return;
    const node = modalityRevealRef.current;
    if (node == null) return;
    // Defer one frame so the freshly-mounted reveal block has been laid out.
    const handle = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(handle);
  }, [innerStep, workModality]);

  const handleValidateRut = useCallback(async (): Promise<void> => {
    const digits = companyRut.replace(/\D/g, "");
    if (digits.length !== 12) {
      toast.error(t("auth.register.rutFormatError"));
      return;
    }
    setIsValidatingRut(true);
    try {
      const data = await validateRut(digits);
      setValidatedCompany(data);
      setCompanyRutManualMode(false);
      setIncomeManualCompanyFields(EMPTY_MANUAL_COMPANY_FIELDS);
      toast.success(t("requests.user.legalEntityRutValidated"));
    } catch (err) {
      setValidatedCompany(null);
      if (isValidateRutServiceUnavailableError(err)) {
        setCompanyRutManualMode(true);
        toast.error(t("requests.user.rutValidationUnavailable"));
      } else {
        setCompanyRutManualMode(false);
        toast.error(t("requests.user.legalEntityRutInvalid"));
      }
    } finally {
      setIsValidatingRut(false);
    }
  }, [companyRut, setIncomeManualCompanyFields, setCompanyRutManualMode, setValidatedCompany, t]);

  const validateStep1 = useCallback((): boolean => {
    if (!fulfillmentMode || !allowedFulfillmentModes.includes(fulfillmentMode)) {
      toast.error(t("requests.user.incomeWizard.validationFulfillment"));
      return false;
    }
    if (!activity) {
      toast.error(t("requests.user.incomeWizard.validationActivityRequired"));
      return false;
    }
    if (!resolvedPresentingEntity) {
      toast.error(t("requests.user.incomeWizard.validationPresentingEntity"));
      return false;
    }
    if (showsPresentingInstitution && !resolvedPresentingInstitution) {
      toast.error(t("requests.user.incomeWizard.validationPresentingInstitution"));
      return false;
    }
    if (activity === "other" && !activityCustom.trim()) {
      toast.error(t("requests.user.incomeWizard.validationActivityOther"));
      return false;
    }
    if (specialFormatRequested && !specialFormatFile) {
      toast.error(t("requests.user.incomeWizard.validationSpecialFormatFile"));
      return false;
    }
    return true;
  }, [
    showsPresentingInstitution,
    resolvedPresentingInstitution,
    activity,
    activityCustom,
    resolvedPresentingEntity,
    specialFormatFile,
    specialFormatRequested,
    allowedFulfillmentModes,
    fulfillmentMode,
    t,
  ]);

  const validateStep2Company = useCallback((): boolean => {
    if (!workModality) {
      toast.error(t("requests.user.incomeWizard.validationModality"));
      return false;
    }
    if (!legalDocumentType) {
      toast.error(t("requests.user.incomeWizard.validationIdDocumentType"));
      return false;
    }
    if (legalDocumentType === "national_id") {
      if (!legalDocumentFrontFile || !legalDocumentBackFile) {
        toast.error(t("requests.user.incomeWizard.validationIdNationalId"));
        return false;
      }
    } else if (!passportDocumentFile) {
      toast.error(t("requests.user.incomeWizard.validationIdPassport"));
      return false;
    }
    if (workModality === "dependent") {
      return true;
    }
    const digits = companyRut.replace(/\D/g, "");
    if (digits.length !== 12 || !incomeEffectiveValidatedCompany) {
      toast.error(t("requests.user.incomeWizard.validationCompanyRut"));
      return false;
    }
    if (!dgiFile || !tarjetaRutFile || !bpsFile || !bpsNumber.trim()) {
      toast.error(t("requests.user.incomeWizard.validationIndependentDocs"));
      return false;
    }
    if (!form6906File) {
      toast.error(t("requests.user.incomeWizard.validationForm6906"));
      return false;
    }
    if (workModality === "professional_independent" && !cajaFile) {
      toast.error(t("requests.user.incomeWizard.validationCaja"));
      return false;
    }
    return true;
  }, [
    workModality,
    companyRut,
    incomeEffectiveValidatedCompany,
    dgiFile,
    tarjetaRutFile,
    form6906File,
    bpsFile,
    bpsNumber,
    cajaFile,
    legalDocumentType,
    legalDocumentFrontFile,
    legalDocumentBackFile,
    passportDocumentFile,
    t,
  ]);

  const validateStep3Uploads = useCallback((): boolean => {
    if (workModality === "dependent") {
      if (salaryReceipts.length < periodMonths) {
        toast.error(t("requests.user.incomeWizard.validationAllReceipts", { count: periodMonths }));
        return false;
      }
      if (salaryReceipts.some((f) => f == null)) {
        toast.error(t("requests.user.incomeWizard.validationAllReceiptsFilled"));
        return false;
      }
      return true;
    }
    if (workModality === "independent" || workModality === "professional_independent") {
      if (invoices.length < periodMonths) {
        toast.error(t("requests.user.incomeWizard.validationAllInvoices", { count: periodMonths }));
        return false;
      }
      if (invoices.some((f) => f == null)) {
        toast.error(t("requests.user.incomeWizard.validationAllInvoicesFilled"));
        return false;
      }
      return true;
    }
    toast.error(t("requests.user.incomeWizard.validationModality"));
    return false;
  }, [workModality, salaryReceipts, invoices, periodMonths, t]);

  const buildPayload = useCallback((): IncomeCertificateSubmitPayload => {
    const details: IncomeCertificateDetailsBody = {
      periodMonths,
      presentingEntity: resolvedPresentingEntity,
      presentingInstitution: resolvedPresentingInstitution || undefined,
      activity,
      specialFormatRequested,
      workModality: workModality as IncomeCertificateWorkModality,
    };
    if (activity === "other") {
      details.activityCustom = activityCustom.trim();
    }
    if (workModality === "independent" || workModality === "professional_independent") {
      details.bpsNumber = bpsNumber.trim();
    }

    const uploads: Array<{ type: UserNotarialRequestDocumentType; file: File }> = [];
    if (specialFormatRequested && specialFormatFile && !lockedFields.has('specialFormatRequested')) {
      uploads.push({ type: "income_special_format_template", file: specialFormatFile });
    }
    if (legalDocumentType === "national_id") {
      if (legalDocumentFrontFile) {
        uploads.push({ type: "legal_document_front", file: legalDocumentFrontFile });
      }
      if (legalDocumentBackFile) {
        uploads.push({ type: "legal_document_back", file: legalDocumentBackFile });
      }
    } else if (legalDocumentType === "passport" && passportDocumentFile) {
      uploads.push({ type: "passport_document", file: passportDocumentFile });
    }
    if (workModality === "dependent") {
      salaryReceipts.forEach((file) => {
        if (file != null) {
          uploads.push({ type: "income_salary_receipt", file });
        }
      });
    } else if (workModality === "independent" || workModality === "professional_independent") {
      if (dgiFile) uploads.push({ type: "income_dgi_certificate", file: dgiFile });
      if (tarjetaRutFile) uploads.push({ type: "income_tarjeta_rut", file: tarjetaRutFile });
      if (form6906File) uploads.push({ type: "income_dgi_form_6906", file: form6906File });
      if (bpsFile) uploads.push({ type: "income_bps_certificate", file: bpsFile });
      invoices.forEach((file) => {
        if (file != null) {
          uploads.push({ type: "income_invoice", file });
        }
      });
      if (workModality === "professional_independent" && cajaFile) {
        uploads.push({ type: "income_professional_caja_certificate", file: cajaFile });
      }
    }

    let companySnapshot: IncomeCertificateSubmitPayload["companySnapshot"];
    const co = incomeEffectiveValidatedCompany;
    if (
      (workModality === "independent" || workModality === "professional_independent") &&
      co
    ) {
      companySnapshot = {
        legalName: co.legalName,
        tradeName: co.tradeName,
        rut: companyRut.replace(/\D/g, "").slice(0, 12),
        legalAddress: co.legalAddress,
        city: co.city,
        department: co.department,
        postalCode: co.postalCode,
        phone: co.phone,
        email: co.email,
        fechaInicio: co.fechaInicio,
        economicActivity: co.economicActivity,
      };
    }

    const companyRutUnverified =
      (workModality === "independent" || workModality === "professional_independent") &&
      companyRutManualMode === true;

    return {
      fulfillmentMode,
      incomeCertificateDetails: details,
      companySnapshot,
      uploads,
      companyRutUnverified: companyRutUnverified === true ? true : undefined,
    };
  }, [
    fulfillmentMode,
    periodMonths,
    resolvedPresentingEntity,
    resolvedPresentingInstitution,
    activity,
    activityCustom,
    specialFormatRequested,
    specialFormatFile,
    workModality,
    bpsNumber,
    salaryReceipts,
    dgiFile,
    tarjetaRutFile,
    form6906File,
    bpsFile,
    invoices,
    cajaFile,
    incomeEffectiveValidatedCompany,
    companyRutManualMode,
    companyRut,
    legalDocumentType,
    legalDocumentFrontFile,
    legalDocumentBackFile,
    passportDocumentFile,
  ]);

  const handlePrimary = useCallback(async (): Promise<void> => {
    if (innerStep === 1) {
      if (!validateStep1()) return;
      onInnerStepChange(2);
      return;
    }
    if (innerStep === 2) {
      if (!validateStep2Company()) return;
      onInnerStepChange(3);
      return;
    }
    if (!validateStep3Uploads()) return;
    const payload = buildPayload();
    if (onStep3ContinueToReview) {
      onStep3ContinueToReview(payload);
      return;
    }
    try {
      const id = await createWithUpload(payload);
      onSuccess(id);
    } catch {
      // Parent / mutation surfaces errors
    }
  }, [
    innerStep,
    validateStep1,
    validateStep2Company,
    validateStep3Uploads,
    onInnerStepChange,
    createWithUpload,
    buildPayload,
    onSuccess,
    onStep3ContinueToReview,
  ]);

  const prepareSubmitPayloadIfValid = useCallback((): IncomeCertificateSubmitPayload | null => {
    if (innerStep !== 3) return null;
    if (!validateStep3Uploads()) return null;
    return buildPayload();
  }, [innerStep, validateStep3Uploads, buildPayload]);

  useImperativeHandle(
    ref,
    () => ({
      runPrimaryAction: handlePrimary,
      prepareSubmitPayloadIfValid,
    }),
    [handlePrimary, prepareSubmitPayloadIfValid],
  );

  const modalityOptions: ChoiceCardOption<IncomeCertificateWorkModality>[] = useMemo(
    () =>
      (
        [
          ["dependent", "modalityDependent"],
          ["independent", "modalityIndependent"],
          ["professional_independent", "modalityProfessional"],
        ] as const
      ).map(([value, key]) => ({
        value,
        label: t(`requests.user.incomeWizard.${key}`),
        hint: t(`requests.user.incomeWizard.${key}Hint`),
        footer: (
          <>
            <ListChecks className="h-3 w-3" />
            <span className="normal-case tracking-normal">
              {t(`requests.user.incomeWizard.${key}Requirements`)}
            </span>
          </>
        ),
      })),
    [t],
  );

  const fulfillmentModeOptions: ChoiceCardOption<string>[] = useMemo(
    () =>
      allowedFulfillmentModes.map((slug) => ({
        value: slug,
        label: t(`requests.user.fulfillmentMode.optionTitle.${slug}`),
        hint: t(`requests.user.fulfillmentMode.optionHint.${slug}`),
      })),
    [allowedFulfillmentModes, t],
  );

  const specialFormatOptions: ChoiceCardOption<"no" | "yes">[] = useMemo(
    () => [
      {
        value: "no",
        label: t("requests.user.incomeWizard.specialFormatNo"),
        hint: t("requests.user.incomeWizard.specialFormatNoHint"),
      },
      {
        value: "yes",
        label: t("requests.user.incomeWizard.specialFormatYes"),
        hint: t("requests.user.incomeWizard.specialFormatYesHint"),
      },
    ],
    [t],
  );

  const headingLead =
    innerStep === 1
      ? t("requests.user.incomeWizard.step1Lead")
      : innerStep === 2
        ? t("requests.user.incomeWizard.step2Lead")
        : workModality === "dependent"
          ? t("requests.user.incomeWizard.step3LeadDependent")
          : t("requests.user.incomeWizard.step3Lead");
  const headingRest =
    innerStep === 1
      ? t("requests.user.incomeWizard.step1Description")
      : innerStep === 2
        ? t("requests.user.incomeWizard.step2Description")
        : t("requests.user.incomeWizard.step3Description");

  return (
    <div className="space-y-6">
      <WizardStepHeading lead={headingLead} rest={headingRest} />

      {innerStep === 1 ? (
        <div className="space-y-4">
          <div className="space-y-3">
            <Label className="text-foreground">{t("requests.user.fulfillmentMode.sectionTitle")}</Label>
            <p className="text-sm text-muted-foreground">{t("requests.user.fulfillmentMode.sectionHint")}</p>
            <ChoiceCardGroup
              value={fulfillmentMode}
              onValueChange={setFulfillmentMode}
              options={fulfillmentModeOptions}
              idPrefix="inc-fulfillment"
              ariaLabel={t("requests.user.fulfillmentMode.sectionTitle")}
              columns={fulfillmentModeOptions.length >= 3 ? 3 : 2}
              disabled={lockedFields.has('fulfillmentMode')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="inc-period">{t("requests.user.incomeWizard.periodLabel")}</Label>
            <Select
              value={String(periodMonths)}
              onValueChange={(v) => setPeriodMonths(Number(v) as 6 | 12 | 24)}
              disabled={lockedFields.has('periodMonths')}
            >
              <SelectTrigger id="inc-period" className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">{t("requests.user.incomeWizard.period6")}</SelectItem>
                <SelectItem value="12">{t("requests.user.incomeWizard.period12")}</SelectItem>
                <SelectItem value="24">{t("requests.user.incomeWizard.period24")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inc-entity-select">{t("requests.user.incomeWizard.presentingEntityLabel")}</Label>
            <Select
              value={presentingEntityKey === "" ? undefined : presentingEntityKey}
              onValueChange={(v) => setPresentingEntityKey(v as PresentingEntityPreset)}
              disabled={lockedFields.has('presentingEntityKey')}
            >
              <SelectTrigger id="inc-entity-select" className="w-full bg-card">
                <SelectValue placeholder={t("requests.user.incomeWizard.presentingEntityPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {PRESENTING_ENTITY_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`requests.user.incomeWizard.presentingEntityOption.${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {presentingEntityKey === "other" ? (
              <div className="space-y-2 pt-1">
                <Label htmlFor="inc-entity-other">
                  {t("requests.user.incomeWizard.presentingEntityOtherLabel")}
                </Label>
                <Input
                  id="inc-entity-other"
                  value={presentingEntityOther}
                  onChange={(e) => setPresentingEntityOther(e.target.value)}
                  placeholder={t("requests.user.incomeWizard.presentingEntityOtherPlaceholder")}
                  maxLength={500}
                  autoComplete="off"
                  disabled={lockedFields.has('presentingEntityOther')}
                />
              </div>
            ) : null}
            {showsPresentingInstitution ? (
              <div className="space-y-2 pt-1">
                <Label htmlFor="inc-institution">
                  {t("requests.user.incomeWizard.presentingInstitutionLabel")}
                </Label>
                <SearchableSelect
                  id="inc-institution"
                  value={presentingInstitution}
                  onValueChange={setPresentingInstitution}
                  options={presentingInstitutionOptions}
                  placeholder={t("requests.user.incomeWizard.presentingInstitutionPlaceholder")}
                  searchPlaceholder={t("requests.user.incomeWizard.presentingInstitutionSearch")}
                  emptyLabel={t("requests.user.incomeWizard.presentingInstitutionEmpty")}
                />
                {presentingInstitution === PRESENTING_INSTITUTION_OTHER ? (
                  <Input
                    id="inc-institution-other"
                    value={presentingInstitutionOther}
                    onChange={(e) => setPresentingInstitutionOther(e.target.value)}
                    placeholder={t(
                      "requests.user.incomeWizard.presentingInstitutionOtherPlaceholder",
                    )}
                    aria-label={t("requests.user.incomeWizard.presentingInstitutionOtherLabel")}
                    maxLength={200}
                    autoComplete="off"
                  />
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="inc-activity">{t("requests.user.incomeWizard.activityLabel")}</Label>
            <Select
              value={activity === "" ? undefined : activity}
              onValueChange={(v) => setActivity(v as IncomeActivityKey)}
            >
              <SelectTrigger id="inc-activity" className="bg-card">
                <SelectValue placeholder={t("requests.user.incomeWizard.activityPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_KEYS.map((key) => (
                  <SelectItem key={key} value={key}>
                    {t(`requests.user.incomeWizard.activity.${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {activity === "other" ? (
            <div className="space-y-2">
              <Label htmlFor="inc-activity-custom">
                {t("requests.user.incomeWizard.activityCustomLabel")}
              </Label>
              <Input
                id="inc-activity-custom"
                value={activityCustom}
                onChange={(e) => setActivityCustom(e.target.value)}
                placeholder={t("requests.user.incomeWizard.activityCustomPlaceholder")}
              />
            </div>
          ) : null}

          {lockedFields.has('specialFormatRequested') ? (
            <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
              <span className="font-medium text-foreground">
                {t("requests.user.incomeWizard.specialFormatLabel")}
              </span>
              <p className="mt-1 text-muted-foreground">
                Plantilla proporcionada por {enterpriseName}
                {enterpriseDefaults?.specialFormatTemplateFilename && (
                  <span className="ml-1">({enterpriseDefaults.specialFormatTemplateFilename})</span>
                )}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-foreground">{t("requests.user.incomeWizard.specialFormatLabel")}</Label>
                <ChoiceCardGroup
                  value={specialFormatRequested ? "yes" : "no"}
                  onValueChange={(v) => {
                    setSpecialFormatRequested(v === "yes");
                    if (v === "no") setSpecialFormatFile(null);
                  }}
                  options={specialFormatOptions}
                  idPrefix="inc-sf"
                  ariaLabel={t("requests.user.incomeWizard.specialFormatLabel")}
                  columns={2}
                />
              </div>

              {specialFormatRequested ? (
                <StyledFileInput
                  id="inc-special-format"
                  accept=".pdf,application/pdf"
                  value={specialFormatFile}
                  onChange={setSpecialFormatFile}
                  label={t("requests.user.incomeWizard.specialFormatFileLabel")}
                  hint={t("requests.user.incomeWizard.pdfHint")}
                />
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {innerStep === 2 ? (
        <div className="space-y-5">
          <ChoiceCardGroup
            value={workModality}
            onValueChange={(v) => {
              setWorkModality(v);
              setValidatedCompany(null);
              setCompanyRut("");
            }}
            options={modalityOptions}
            idPrefix="inc-mod"
            ariaLabel={t("requests.user.incomeWizard.modalityLabel")}
            columns={3}
          />

          {workModality ? (
            <div
              key={`identity-${workModality}`}
              ref={modalityRevealRef}
              className="wizard-reveal scroll-mt-4 space-y-5"
            >
              <Separator />
              <Card className="border-border/80 shadow-sm dark:border-border/80 dark:bg-secondary/35 dark:shadow-md dark:shadow-black/20">
                <CardHeader className="space-y-2 pb-2">
                  <CardTitle className="text-lg font-semibold leading-snug tracking-tight text-foreground">
                    {t("requests.user.legalEntityIdentitySectionTitle")}
                  </CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    {t("requests.user.legalEntityIdentitySectionHint")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 pt-2">
                  <div className="space-y-2">
                    <Label htmlFor="inc-id-doc-type" className="text-sm font-medium text-muted-foreground">
                      {t("requests.user.legalDocumentTypeLabel")}
                    </Label>
                    <Select
                      value={legalDocumentType === "" ? undefined : legalDocumentType}
                      onValueChange={(v) => handleLegalDocumentTypeChange(v as LegalDocumentType)}
                    >
                      <SelectTrigger
                        id="inc-id-doc-type"
                        className="w-full max-w-md bg-card transition-colors duration-150"
                      >
                        <SelectValue
                          placeholder={t("requests.user.incomeWizard.idDocumentTypePlaceholder")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="national_id" className="cursor-pointer">
                          {t("requests.user.legalDocumentNationalId")}
                        </SelectItem>
                        <SelectItem value="passport" className="cursor-pointer">
                          {t("requests.user.legalDocumentPassport")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {legalDocumentType === "national_id" ? (
                    <div className="space-y-5 border-t border-border/70 pt-5">
                      <StyledFileInput
                        id="inc-id-front"
                        accept=".pdf,image/*"
                        value={legalDocumentFrontFile}
                        onChange={setLegalDocumentFrontFile}
                        label={t("requests.user.requestTypes.document_legal_document_front")}
                        hint={t("requests.user.legalEntityFileHint")}
                      />
                      <StyledFileInput
                        id="inc-id-back"
                        accept=".pdf,image/*"
                        value={legalDocumentBackFile}
                        onChange={setLegalDocumentBackFile}
                        label={t("requests.user.requestTypes.document_legal_document_back")}
                        hint={t("requests.user.legalEntityFileHint")}
                      />
                    </div>
                  ) : legalDocumentType === "passport" ? (
                    <div className="border-t border-border/70 pt-5">
                      <StyledFileInput
                        id="inc-id-passport"
                        accept=".pdf,image/*"
                        value={passportDocumentFile}
                        onChange={setPassportDocumentFile}
                        label={t("requests.user.requestTypes.document_passport_document")}
                        hint={t("requests.user.legalEntityFileHint")}
                      />
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            </div>
          ) : null}

          {workModality === "independent" || workModality === "professional_independent" ? (
            <div key={`company-${workModality}`} className="wizard-reveal space-y-6">
              <Separator />
              <div className="space-y-1">
                <h3 className="text-lg font-semibold leading-snug tracking-tight text-foreground">
                  {t("requests.user.incomeWizard.companySectionTitle")}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t("requests.user.incomeWizard.companySectionHint")}
                </p>
              </div>
              <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inc-co-rut">{t("requests.user.incomeWizard.companyRutLabel")}</Label>
                <div className="flex gap-2">
                  <Input
                    id="inc-co-rut"
                    value={companyRut}
                    onChange={(e) => {
                      setCompanyRut(e.target.value.replace(/\D/g, "").slice(0, 12));
                      setValidatedCompany(null);
                      setCompanyRutManualMode(false);
                      setIncomeManualCompanyFields(EMPTY_MANUAL_COMPANY_FIELDS);
                    }}
                    placeholder={t("requests.user.legalEntityRutPlaceholder")}
                    inputMode="numeric"
                    maxLength={12}
                    className="flex-1 bg-card"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleValidateRut()}
                    disabled={isValidatingRut}
                  >
                    {isValidatingRut ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                        {t("requests.user.validatingRut")}
                      </>
                    ) : (
                      t("requests.user.validateRutButton")
                    )}
                  </Button>
                </div>
              </div>

              {companyRutManualMode && validatedCompany == null ? (
                <div className="space-y-3 rounded-xl border border-border/80 bg-muted/20 p-4 dark:border-border/80 dark:bg-secondary/25">
                  <p className="text-sm text-muted-foreground">{t("requests.user.manualCompanyIntro")}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="inc-manual-legal-name" className="text-sm font-medium text-foreground">
                        {t("requests.user.manualCompanyLegalName")}
                      </Label>
                      <Input
                        id="inc-manual-legal-name"
                        value={incomeManualCompanyFields.legalName}
                        onChange={(e) => updateIncomeManualCompanyField("legalName", e.target.value)}
                        className="bg-card"
                        autoComplete="organization"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="inc-manual-address" className="text-sm font-medium text-foreground">
                        {t("requests.user.manualCompanyAddress")}
                      </Label>
                      <Input
                        id="inc-manual-address"
                        value={incomeManualCompanyFields.legalAddress}
                        onChange={(e) => updateIncomeManualCompanyField("legalAddress", e.target.value)}
                        className="bg-card"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inc-manual-city" className="text-sm font-medium text-foreground">
                        {t("requests.user.manualCompanyCity")}
                      </Label>
                      <Input
                        id="inc-manual-city"
                        value={incomeManualCompanyFields.city}
                        onChange={(e) => updateIncomeManualCompanyField("city", e.target.value)}
                        className="bg-card"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inc-manual-dept" className="text-sm font-medium text-foreground">
                        {t("requests.user.manualCompanyDepartment")}
                      </Label>
                      <Input
                        id="inc-manual-dept"
                        value={incomeManualCompanyFields.department}
                        onChange={(e) => updateIncomeManualCompanyField("department", e.target.value)}
                        className="bg-card"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inc-manual-phone" className="text-sm font-medium text-foreground">
                        {t("requests.user.manualCompanyPhone")}
                      </Label>
                      <Input
                        id="inc-manual-phone"
                        value={incomeManualCompanyFields.phone}
                        onChange={(e) => updateIncomeManualCompanyField("phone", e.target.value)}
                        className="bg-card"
                        inputMode="tel"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inc-manual-email" className="text-sm font-medium text-foreground">
                        {t("requests.user.manualCompanyEmail")}
                      </Label>
                      <Input
                        id="inc-manual-email"
                        value={incomeManualCompanyFields.email}
                        onChange={(e) => updateIncomeManualCompanyField("email", e.target.value)}
                        className="bg-card"
                        type="email"
                        autoComplete="email"
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {incomeEffectiveValidatedCompany ? (
                <div className="wizard-summary-panel p-4">
                  <p className="text-sm font-semibold text-foreground">
                    {incomeEffectiveValidatedCompany.legalName}
                  </p>
                  {incomeEffectiveValidatedCompany.legalAddress ? (
                    <p className="text-xs text-muted-foreground">
                      {incomeEffectiveValidatedCompany.legalAddress}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <IncomeDocumentGuideSection
                title={t("requests.user.incomeWizard.dgiGuideTitle")}
                instructions={
                  <>
                    <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
                      <li>{t("requests.user.incomeWizard.dgiGuideStep1")}</li>
                      <li>{t("requests.user.incomeWizard.dgiGuideStep2")}</li>
                      <li>{t("requests.user.incomeWizard.dgiGuideStep3")}</li>
                    </ol>
                    <IncomeGuideExternalLink href={DGI_PORTAL_URL}>
                      {t("requests.user.incomeWizard.dgiGuideOpenPortal")}
                    </IncomeGuideExternalLink>
                  </>
                }
                fileUpload={
                  <StyledFileInput
                    id="inc-dgi"
                    accept=".pdf,image/*"
                    value={dgiFile}
                    onChange={setDgiFile}
                    label={t("requests.user.incomeWizard.dgiCertLabel")}
                    hint={t("requests.user.incomeWizard.dgiCertHint")}
                  />
                }
              />
              <IncomeDocumentGuideSection
                title={t("requests.user.incomeWizard.tarjetaRutGuideTitle")}
                instructions={
                  <>
                    <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
                      <li>{t("requests.user.incomeWizard.tarjetaRutGuideStep1")}</li>
                      <li>{t("requests.user.incomeWizard.tarjetaRutGuideStep2")}</li>
                      <li>{t("requests.user.incomeWizard.tarjetaRutGuideStep3")}</li>
                    </ol>
                    <IncomeGuideExternalLink href={DGI_PORTAL_URL}>
                      {t("requests.user.incomeWizard.tarjetaRutGuideOpenPortal")}
                    </IncomeGuideExternalLink>
                  </>
                }
                fileUpload={
                  <StyledFileInput
                    id="inc-tarjeta"
                    accept=".pdf,image/*"
                    value={tarjetaRutFile}
                    onChange={setTarjetaRutFile}
                    label={t("requests.user.incomeWizard.tarjetaRutLabel")}
                    hint={t("requests.user.incomeWizard.tarjetaRutHint")}
                  />
                }
              />
              <IncomeDocumentGuideSection
                title={t("requests.user.incomeWizard.form6906GuideTitle")}
                instructions={
                  <>
                    <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
                      <li>{t("requests.user.incomeWizard.form6906GuideStep1")}</li>
                      <li>{t("requests.user.incomeWizard.form6906GuideStep2")}</li>
                      <li>{t("requests.user.incomeWizard.form6906GuideStep3")}</li>
                      <li>{t("requests.user.incomeWizard.form6906GuideStep4")}</li>
                    </ol>
                    <IncomeGuideExternalLink href={DGI_PORTAL_URL}>
                      {t("requests.user.incomeWizard.form6906GuideOpenPortal")}
                    </IncomeGuideExternalLink>
                  </>
                }
                fileUpload={
                  <StyledFileInput
                    id="inc-form-6906"
                    accept=".pdf,image/*"
                    value={form6906File}
                    onChange={setForm6906File}
                    label={t("requests.user.incomeWizard.form6906Label")}
                    hint={t("requests.user.incomeWizard.form6906Hint")}
                  />
                }
              />
            </div>

            <Separator />
            <div className="space-y-1">
              <h3 className="text-lg font-semibold leading-snug tracking-tight text-foreground">
                {t("requests.user.incomeWizard.bpsSectionTitle")}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {t("requests.user.incomeWizard.bpsSectionHint")}
              </p>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inc-bps-num">{t("requests.user.incomeWizard.bpsNumberLabel")}</Label>
                <Input
                  id="inc-bps-num"
                  value={bpsNumber}
                  onChange={(e) => setBpsNumber(e.target.value)}
                  placeholder={t("requests.user.incomeWizard.bpsNumberPlaceholder")}
                />
              </div>
              <IncomeDocumentGuideSection
                title={t("requests.user.incomeWizard.bpsGuideTitle")}
                instructions={
                  <>
                    <ol className="list-decimal space-y-2 pl-5 text-muted-foreground">
                      <li>{t("requests.user.incomeWizard.bpsGuideStep1")}</li>
                      <li>{t("requests.user.incomeWizard.bpsGuideStep2")}</li>
                      <li>{t("requests.user.incomeWizard.bpsGuideStep3")}</li>
                    </ol>
                    <IncomeGuideExternalLink href={BPS_ONLINE_SERVICES_URL}>
                      {t("requests.user.incomeWizard.bpsGuideOpenPortal")}
                    </IncomeGuideExternalLink>
                  </>
                }
                fileUpload={
                  <StyledFileInput
                    id="inc-bps-cert"
                    accept=".pdf,image/*"
                    value={bpsFile}
                    onChange={setBpsFile}
                    label={t("requests.user.incomeWizard.bpsCertLabel")}
                    hint={t("requests.user.incomeWizard.bpsCertHint")}
                  />
                }
              />

            </div>
            </div>
          ) : null}

          {workModality === "professional_independent" ? (
            <div key={`cjppu-${workModality}`} className="wizard-reveal space-y-4">
              <Separator />
              <div className="space-y-1">
                <h3 className="text-lg font-semibold leading-snug tracking-tight text-foreground">
                  {t("requests.user.incomeWizard.cjppuSectionTitle")}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t("requests.user.incomeWizard.cjppuSectionHint")}
                </p>
              </div>
              <IncomeDocumentGuideSection
                title={t("auth.acceptAccountantInvitation.certificateSectionTitle")}
                instructions={<CjppuCertificateInstructionSteps variant="generic" />}
                fileUpload={
                  <StyledFileInput
                    id="inc-caja"
                    accept=".pdf,application/pdf"
                    value={cajaFile}
                    onChange={setCajaFile}
                    label={t("requests.user.incomeWizard.cjppuCertLabel")}
                    hint={t("auth.acceptAccountantInvitation.certificateFileHelp")}
                  />
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {innerStep === 3 && workModality === "dependent" ? (
        <IncomeUploadCard
          icon={Receipt}
          title={t("requests.user.incomeWizard.receiptsTitle")}
          description={t("requests.user.incomeWizard.receiptsHint", { count: periodMonths })}
        >
          <div className="space-y-5">
            {salaryReceipts.map((file, index) => (
              <div key={`receipt-${index}`} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label
                    htmlFor={`inc-receipt-${index}`}
                    className="text-sm font-medium text-foreground"
                  >
                    {t("requests.user.incomeWizard.receiptSlotLabel", { index: index + 1 })}
                  </Label>
                  {index >= periodMonths ? (
                    <button
                      type="button"
                      aria-label={t("requests.user.incomeWizard.removeDocumentSlot")}
                      onClick={() => {
                        setSalaryReceipts((prev) => prev.filter((_, i) => i !== index));
                      }}
                      className="-mr-1 rounded p-1 text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                <StyledFileInput
                  id={`inc-receipt-${index}`}
                  accept=".pdf,image/*"
                  value={file}
                  onChange={(f) => {
                    setSalaryReceipts((prev) => {
                      const next = [...prev];
                      next[index] = f;
                      return next;
                    });
                  }}
                  hint={t("requests.user.incomeWizard.receiptSlotHint")}
                />
              </div>
            ))}
            {salaryReceipts.length < MAX_INCOME_CERTIFICATE_BILLABLE_FILES ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-2 border-dashed border-border/90 bg-background/60 text-foreground transition-colors duration-150 hover:border-primary/45 hover:bg-muted/40 hover:text-foreground focus-visible:text-foreground dark:bg-muted/50 dark:hover:bg-muted dark:hover:text-foreground sm:w-fit sm:border-solid"
                onClick={() => {
                  setSalaryReceipts((prev) => [...prev, null]);
                }}
              >
                <Plus className="h-4 w-4" />
                {t("requests.user.incomeWizard.addMonthlyDocument")}
              </Button>
            ) : null}
          </div>
        </IncomeUploadCard>
      ) : null}

      {innerStep === 3 &&
      (workModality === "independent" || workModality === "professional_independent") ? (
        <IncomeUploadCard
          icon={FileText}
          title={t("requests.user.incomeWizard.invoicesTitle")}
          description={t("requests.user.incomeWizard.invoicesHint", { count: periodMonths })}
        >
          <div className="space-y-5">
            {invoices.map((file, index) => (
              <div key={`inv-${index}`} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label
                    htmlFor={`inc-inv-${index}`}
                    className="text-sm font-medium text-foreground"
                  >
                    {t("requests.user.incomeWizard.invoiceSlotLabel", { index: index + 1 })}
                  </Label>
                  {index >= periodMonths ? (
                    <button
                      type="button"
                      aria-label={t("requests.user.incomeWizard.removeDocumentSlot")}
                      onClick={() => {
                        setInvoices((prev) => prev.filter((_, i) => i !== index));
                      }}
                      className="-mr-1 rounded p-1 text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                <StyledFileInput
                  id={`inc-inv-${index}`}
                  accept=".pdf,image/*"
                  value={file}
                  onChange={(f) => {
                    setInvoices((prev) => {
                      const next = [...prev];
                      next[index] = f;
                      return next;
                    });
                  }}
                  hint={t("requests.user.incomeWizard.invoiceSlotHint")}
                />
              </div>
            ))}
            {invoices.length < MAX_INCOME_CERTIFICATE_BILLABLE_FILES ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full gap-2 border-dashed border-border/90 bg-background/60 text-foreground transition-colors duration-150 hover:border-primary/45 hover:bg-muted/40 hover:text-foreground focus-visible:text-foreground dark:bg-muted/50 dark:hover:bg-muted dark:hover:text-foreground sm:w-fit sm:border-solid"
                onClick={() => {
                  setInvoices((prev) => [...prev, null]);
                }}
              >
                <Plus className="h-4 w-4" />
                {t("requests.user.incomeWizard.addMonthlyDocument")}
              </Button>
            ) : null}
          </div>
        </IncomeUploadCard>
      ) : null}
    </div>
  );
});
